import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ArrowLeft, FileText, Loader2, CheckCircle, Edit2, Save,
  MapPin, Phone, User, Truck, Package, PenTool, AlertTriangle,
  Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const DriverBOLPage = () => {
  const navigate = useNavigate();
  const { loadId } = useParams();
  const [bol, setBol] = useState(null);
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signatureType, setSignatureType] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver/login');
      return;
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchData();
  }, [loadId, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // First get load details
      const loadRes = await api.get(`/driver/loads/${loadId}`);
      setLoad(loadRes.data);

      // Then try to get existing BOL
      try {
        const bolRes = await api.get(`/driver/bol/${loadId}`);
        if (bolRes.data) {
          setBol(bolRes.data);
          setFormData(bolRes.data);
        }
      } catch (e) {
        // No BOL exists yet, that's ok
        setBol(null);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/driver/login');
      } else if (error.response?.status === 403) {
        toast.error('You are not assigned to this load');
        navigate('/driver/dashboard');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBOL = async () => {
    setSaving(true);
    try {
      const res = await api.post('/driver/bol', {
        load_id: loadId,
        shipper_name: load?.shipper_name,
        shipper_address: load?.pickup_location,
        shipper_phone: load?.shipper_phone,
        consignee_address: load?.delivery_location,
        vehicles: load?.vehicle_info ? [{ make_model: load.vehicle_info }] : []
      });
      
      toast.success(`BOL ${res.data.bol_number} created`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create BOL');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await api.put(`/driver/bol/${bol.id}`, formData);
      toast.success('BOL updated');
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const openSignatureDialog = (type) => {
    setSignatureType(type);
    setShowSignDialog(true);
    // Clear canvas after dialog opens
    setTimeout(() => {
      if (signatureCanvasRef.current) {
        const ctx = signatureCanvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, signatureCanvasRef.current.width, signatureCanvasRef.current.height);
      }
    }, 100);
  };

  const handleSign = async () => {
    const canvas = signatureCanvasRef.current;
    const signatureData = canvas?.toDataURL() || '';
    
    if (!signatureData || signatureData === 'data:,') {
      toast.error('Please draw your signature');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/driver/bol/${bol.id}/sign`, {
        signature_type: signatureType,
        signature_data: signatureData,
        notes: null
      });
      
      toast.success('Signature added');
      setShowSignDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add signature');
    } finally {
      setSaving(false);
    }
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#4ADE80';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500/20 text-gray-400';
      case 'signed_pickup': return 'bg-blue-500/20 text-blue-400';
      case 'in_transit': return 'bg-yellow-500/20 text-yellow-400';
      case 'signed_delivery': return 'bg-purple-500/20 text-purple-400';
      case 'completed': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getSignatureLabel = (type) => {
    switch (type) {
      case 'shipper': return 'Shipper Signature';
      case 'driver_pickup': return 'Driver Pickup Signature';
      case 'consignee': return 'Consignee Signature';
      case 'driver_delivery': return 'Driver Delivery Signature';
      default: return 'Signature';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  // No BOL exists - show create button
  if (!bol) {
    return (
      <div className="min-h-screen bg-[#071423]">
        {/* Header */}
        <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/driver/load/${loadId}`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-white">Bill of Lading</h1>
              <p className="text-xs text-gray-400">{load?.pickup_location?.split(',')[0]} to {load?.delivery_location?.split(',')[0]}</p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="p-8 text-center">
              <FileText className="w-16 h-16 mx-auto text-[#3B82F6] mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Create Bill of Lading</h2>
              <p className="text-gray-400 mb-6">
                Generate a digital BOL for this load with all the shipment details pre-filled.
              </p>
              
              {/* Load Summary */}
              <div className="bg-[#0A1628] rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-400 mb-2">Load Details</p>
                <div className="space-y-1 text-sm">
                  <p className="text-white"><span className="text-gray-500">From:</span> {load?.pickup_location}</p>
                  <p className="text-white"><span className="text-gray-500">To:</span> {load?.delivery_location}</p>
                  {load?.vehicle_info && (
                    <p className="text-white"><span className="text-gray-500">Vehicle:</span> {load.vehicle_info}</p>
                  )}
                </div>
              </div>

              <Button 
                className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                onClick={handleCreateBOL}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create BOL
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071423] pb-24">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/driver/load/${loadId}`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-white">{bol.bol_number}</h1>
              <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(bol.status)}`}>
                {bol.status?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>
          {!isEditing ? (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setIsEditing(false); setFormData(bol); }}>
                <X className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                className="bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Shipper Info */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-green-400 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Shipper (Origin)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {isEditing ? (
              <>
                <div>
                  <Label className="text-xs text-gray-500">Name/Company</Label>
                  <Input 
                    value={formData.shipper_name || ''} 
                    onChange={(e) => setFormData({...formData, shipper_name: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Address</Label>
                  <Input 
                    value={formData.shipper_address || ''} 
                    onChange={(e) => setFormData({...formData, shipper_address: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <Input 
                    value={formData.shipper_phone || ''} 
                    onChange={(e) => setFormData({...formData, shipper_phone: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-white font-medium">{bol.shipper_name || '-'}</p>
                <p className="text-sm text-gray-400">{bol.shipper_address}</p>
                {bol.shipper_phone && (
                  <p className="text-sm text-gray-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {bol.shipper_phone}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Consignee Info */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-400 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Consignee (Destination)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {isEditing ? (
              <>
                <div>
                  <Label className="text-xs text-gray-500">Name/Company</Label>
                  <Input 
                    value={formData.consignee_name || ''} 
                    onChange={(e) => setFormData({...formData, consignee_name: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Address</Label>
                  <Input 
                    value={formData.consignee_address || ''} 
                    onChange={(e) => setFormData({...formData, consignee_address: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <Input 
                    value={formData.consignee_phone || ''} 
                    onChange={(e) => setFormData({...formData, consignee_phone: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-white font-medium">{bol.consignee_name || '-'}</p>
                <p className="text-sm text-gray-400">{bol.consignee_address}</p>
                {bol.consignee_phone && (
                  <p className="text-sm text-gray-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {bol.consignee_phone}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Carrier/Driver Info */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-blue-400 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Carrier Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Carrier:</span>
                <p className="text-white">{bol.carrier_name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">MC#:</span>
                <p className="text-white">{bol.carrier_mc || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Driver:</span>
                <p className="text-white">{bol.driver_name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Truck#:</span>
                {isEditing ? (
                  <Input 
                    value={formData.truck_number || ''} 
                    onChange={(e) => setFormData({...formData, truck_number: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20 h-8"
                    placeholder="Enter truck #"
                  />
                ) : (
                  <p className="text-white">{bol.truck_number || '-'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle/Cargo */}
        {bol.vehicles && bol.vehicles.length > 0 && (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-purple-400 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Vehicles/Cargo
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {bol.vehicles.map((v, idx) => (
                <div key={idx} className="bg-[#0A1628] rounded p-2 text-sm">
                  <p className="text-white">{v.year} {v.make_model}</p>
                  {v.vin && <p className="text-gray-400 text-xs">VIN: {v.vin}</p>}
                  {v.condition && <p className="text-gray-400 text-xs">Condition: {v.condition}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Signatures Section */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-[#4ADE80] flex items-center gap-2">
              <PenTool className="w-4 h-4" />
              Signatures
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {/* Pickup Signatures */}
            <div className="border-b border-[#3B82F6]/10 pb-3">
              <p className="text-xs text-gray-500 mb-2 uppercase">Pickup</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#0A1628] rounded p-2">
                  <p className="text-xs text-gray-400">Shipper</p>
                  {bol.shipper_signature ? (
                    <div className="flex items-center gap-1 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" /> Signed
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-1 w-full text-xs"
                      onClick={() => openSignatureDialog('shipper')}
                    >
                      Capture
                    </Button>
                  )}
                </div>
                <div className="bg-[#0A1628] rounded p-2">
                  <p className="text-xs text-gray-400">Driver</p>
                  {bol.driver_pickup_signature ? (
                    <div className="flex items-center gap-1 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" /> Signed
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-1 w-full text-xs"
                      onClick={() => openSignatureDialog('driver_pickup')}
                    >
                      Sign
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Delivery Signatures */}
            <div>
              <p className="text-xs text-gray-500 mb-2 uppercase">Delivery</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#0A1628] rounded p-2">
                  <p className="text-xs text-gray-400">Consignee</p>
                  {bol.consignee_signature ? (
                    <div className="flex items-center gap-1 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" /> Signed
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-1 w-full text-xs"
                      onClick={() => openSignatureDialog('consignee')}
                    >
                      Capture
                    </Button>
                  )}
                </div>
                <div className="bg-[#0A1628] rounded p-2">
                  <p className="text-xs text-gray-400">Driver</p>
                  {bol.driver_delivery_signature ? (
                    <div className="flex items-center gap-1 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" /> Signed
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-1 w-full text-xs"
                      onClick={() => openSignatureDialog('driver_delivery')}
                    >
                      Sign
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Condition Notes */}
        {(bol.pickup_condition_notes || bol.delivery_condition_notes) && (
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Condition Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2 text-sm">
              {bol.pickup_condition_notes && (
                <div>
                  <span className="text-gray-500">Pickup:</span>
                  <p className="text-white">{bol.pickup_condition_notes}</p>
                </div>
              )}
              {bol.delivery_condition_notes && (
                <div>
                  <span className="text-gray-500">Delivery:</span>
                  <p className="text-white">{bol.delivery_condition_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Signature Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>{getSignatureLabel(signatureType)}</DialogTitle>
            <DialogDescription>Draw signature below</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border border-[#3B82F6]/20 rounded-lg overflow-hidden bg-[#0A1628]">
              <canvas
                ref={signatureCanvasRef}
                width={280}
                height={150}
                className="w-full touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearSignature}
              className="text-xs text-gray-400"
            >
              Clear Signature
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSignDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSign}
                disabled={saving}
                className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Signature'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverBOLPage;
